import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { getToken } from '../services/otpAuthService';
import { sessioneDiDipendente } from '../utils/sezioniDipendente';

const SOCKET_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '').replace('/api', '')
  : 'http://localhost:5000';

interface SvincoloMessage {
  id: string;
  attoId: string;
  mittente: {
    _id: string;
    nominativo: string;
    codice: string;
  };
  testo: string;
  tipo: 'messaggio' | 'sistema';
  fase: 'pre_assegnazione' | 'post_assegnazione';
  createdAt: string;
}

interface OnlineUser {
  id: string;
  name: string;
  codice?: string;
  isAdmin: boolean;
}

interface UseSvincoloAttiSocketOptions {
  attoId: string;
  onNewMessage?: (message: SvincoloMessage) => void;
  enabled?: boolean;
}

const isAdminUser = (): boolean => {
  return !!localStorage.getItem('accessToken');
};

const getAdminToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

export const useSvincoloAttiSocket = ({
  attoId,
  onNewMessage,
  enabled = true
}: UseSvincoloAttiSocketOptions) => {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [typingUsers, setTypingUsers] = useState<{ id: string; name: string }[]>([]);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onNewMessageRef = useRef(onNewMessage);
  onNewMessageRef.current = onNewMessage;

  useEffect(() => {
    if (!enabled || !attoId) return;

    const isAdmin = isAdminUser();
    const token = isAdmin ? getAdminToken() : getToken();

    if (!token) return;
    // Il namespace accetta solo il token del titolare: il dipendente non apre il socket (la pagina va col REST)
    if (!isAdmin && sessioneDiDipendente()) return;

    socketRef.current = io(`${SOCKET_URL}/svincolo-atti`, {
      auth: {
        token,
        tokenType: isAdmin ? 'admin' : 'associate'
      },
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      setConnected(true);
      socketRef.current?.emit('join_svincolo_room', { attoId });
    });

    socketRef.current.on('disconnect', () => {
      setConnected(false);
    });

    socketRef.current.on('error', (err: { message: string }) => {
      console.error('Svincolo socket error:', err.message);
    });

    socketRef.current.on('svincolo_new_message', (msg: SvincoloMessage) => {
      onNewMessageRef.current?.(msg);
    });

    socketRef.current.on('svincolo_online_users', ({ users }: { users: OnlineUser[] }) => {
      setOnlineUsers(users);
    });

    socketRef.current.on('svincolo_user_joined', ({ user }: { user: OnlineUser }) => {
      setOnlineUsers(prev => {
        if (prev.some(u => u.id === user.id)) return prev;
        return [...prev, user];
      });
    });

    socketRef.current.on('svincolo_user_left', ({ userId }: { userId: string }) => {
      setOnlineUsers(prev => prev.filter(u => u.id !== userId));
    });

    socketRef.current.on('svincolo_user_typing', ({ user }: { user: OnlineUser }) => {
      setTypingUsers(prev => {
        if (prev.some(t => t.id === user.id)) return prev;
        return [...prev, { id: user.id, name: user.name }];
      });
    });

    socketRef.current.on('svincolo_user_stopped_typing', ({ userId }: { userId: string }) => {
      setTypingUsers(prev => prev.filter(t => t.id !== userId));
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_svincolo_room', { attoId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [attoId, enabled]);

  const sendMessage = useCallback((content: string) => {
    if (!socketRef.current || !connected || !content.trim()) return false;
    socketRef.current.emit('send_svincolo_message', { attoId, content: content.trim() });
    return true;
  }, [attoId, connected]);

  const startTyping = useCallback(() => {
    if (!socketRef.current || !connected) return;
    socketRef.current.emit('svincolo_typing_start', { attoId });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socketRef.current?.emit('svincolo_typing_stop', { attoId });
    }, 2000);
  }, [attoId, connected]);

  const stopTyping = useCallback(() => {
    if (!socketRef.current || !connected) return;
    socketRef.current.emit('svincolo_typing_stop', { attoId });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
  }, [attoId, connected]);

  const markAsRead = useCallback(() => {
    if (!socketRef.current || !connected) return;
    socketRef.current.emit('svincolo_mark_read', { attoId });
  }, [attoId, connected]);

  return { connected, onlineUsers, typingUsers, sendMessage, startTyping, stopTyping, markAsRead };
};

export default useSvincoloAttiSocket;
